<template>
  <div class="quill-css-mob">
    <div class="position-relative">
      <quillEditor v-model="postDescription" ref="quillEditor" :options="editorOptions" class="createpost-quill" @focus="handleQFocus" @blur="handleQBlur" />
      <template v-if="optionsShown">
        <CustomEmojiPicker
          class="position-absolute editor-emoji"
          :visible="emojiVisible"
          :handleVisible="handleEmojiVisible"
          :insert="insert"
          :customColor="'#94a3b8 !important'"
          @click="() => showOptions()"
        />
        <a-divider type="vertical" class="position-absolute hide-in-mobile" :style="{ left: '50px', bottom: '7.5px', height: '24px', top: 'auto' }" />
        <div id="counter" class="position-absolute d-flex align-items-center">
          <span :class="`${$refs.quillEditor && $refs.quillEditor.quill.getText().length - 1 > 5000 ? 'error500' : 'gray400'} font12`">
            {{ $refs.quillEditor ? $refs.quillEditor.quill.getText().length - 1 : "0" }}
          </span>
          <span class="gray400 font12">/5000</span>
        </div>
      </template>
    </div>

    <div v-if="selectedLinkToParse" @click="openLink" class="cp">
      <div class="position-relative">
        <img v-if="parsedLinkDetails.image" :src="parsedLinkDetails.image" alt="parsed-link-img" :style="{ 'object-fit': 'contain', height: '278px', width: '100%' }" />
        <div v-else class="h45 bg-gray100"></div>
        <div class="position-absolute d-flex align-items-center justify-content-between p-2 w-100" :style="{ top: 0 }">
          <div class="ml-2 cp br8 px-2 py-1 gap4 d-flex align-items-center bg-gray100" @click.stop="changeScreen('linkpreview')">
            <font-awesome-icon :icon="['far', 'repeat']" class="gray900 font20" />
            <p class="mb-0 gray900 fw700 font14">Change preview</p>
          </div>
          <font-awesome-icon :icon="['far', 'times']" class="gray500 bg-gray100 p-1 ar1 font20 rounded-circle cp" @click="selectLink('')" />
        </div>
      </div>
      <div class="bg-gray100 p-3" :style="{ 'border-radius': '0px 0px 8px 8px' }">
        <p class="mb-0 gray900 fw700 font16">{{ parsedLinkDetails.title || "" }}</p>
        <p class="mb-0 gray600 font16">{{ parsedLinkDetails.shortened_url || "" }}</p>
      </div>
    </div>

    <!-- ADDING IMAGES -->
    <template v-if="addingImages">
      <div v-if="isImageUploading" class="d-flex justify-content-center mt-2">
        <div>
          <div class="spinner-border position-absolute spinner-class"></div>
        </div>
        <div :style="{ 'margin-left': '-33px', 'margin-top': '40px' }">Loading...</div>
      </div>
      <template v-else-if="!fileList.length && !isImageUploading">
        <div class="imageContainer mt-3" @click="handleImageUpload">
          <font-awesome-icon
            :icon="['far', 'times']"
            class="gray500 position-absolute bg-gray100 p-1 ar1 font20 rounded-circle"
            :style="{ top: '10px', right: '10px', 'z-index': 6 }"
            @click.stop="
              () => {
                handleCancelImage(true)
                videoAdded = false
              }
            "
          />
          <font-awesome-icon :icon="['far', 'plus']" :style="{ color: getPrimaryColor() }" />
          <p class="my-1 fw700 font14">Add Photos/Videos</p>
          <p class="gray500 font12 mb-0 hide-in-mobile">or drag and drop</p>
          <input
            class="h-100 w-100 camera-icon-inp"
            type="file"
            accept="image/png, image/jpeg, image/gif, image/svg+xml, video/*"
            ref="fileInput"
            @change="handleImageUpload()"
            :style="{ right: 0 }"
            title=""
          />
        </div>
        <p class="mt-4 mb-0 gray600 font12">
          Please don’t upload images that may cause others discomfort.
          <a :href="`${SSO_BASE_URL}/help-center/faq/mycircles-privacy-policy`" target="_blank" class="gray600 font12" :style="{ color: getPrimaryColor() }"> See Community Guidelines.</a>
        </p>
      </template>
      <div :class="`position-relative ${fileList.length > 2 ? 'multiImgs' : ''}`" v-else>
        <p class="mb-0 fw700 font14 gray900 py-1 px-2 bg-gray100 br8 position-absolute cp font16" :style="{ top: '8px', left: '8px', 'z-index': 2 }">
          <font-awesome-icon :icon="['fal', 'images']" class="gray900 mr-1" />
          Add more images
          <input
            class="h-100 w-100 camera-icon-inp"
            type="file"
            accept="image/png, image/jpeg, image/gif, image/svg+xml, video/*"
            ref="fileInput"
            @change="handleImageUpload()"
            :style="{ right: 0 }"
            title=""
          />
        </p>
        <p class="mb-0 fw700 font14 gray900 bg-gray100 br8 position-absolute cp" :style="{ top: '8px', left: '180px', 'z-index': 2, padding: '5.5px 0.5rem' }" @click="changeScreen('pictures')">
          Edit
        </p>
        <font-awesome-icon
          :icon="['far', 'times']"
          class="gray500 position-absolute bg-gray100 p-1 ar1 font20 rounded-circle cp"
          :style="{ top: '16px', right: '16px', 'z-index': 10 }"
          @click="
            () => {
              handleCancelImage()
              videoAdded = false
            }
          "
        />
        <template v-if="fileList.length">
          <div
            v-for="(imgFile, ind) in fileList"
            :key="imgFile"
            :class="`position-relative ${getClass(ind)}`"
            :style="fileList.length === 2 && ind === 1 ? { left: '50%', transform: 'translateX(-50%)', margin: 'auto' } : {}"
          >
            <img v-if="imgFile.type.includes('image')" :src="imgFile.url" :class="`${fileList.length > 2 ? '' : 'w-100'}`" />
            <video v-if="imgFile.type.includes('video')" :src="imgFile.url" controls class="w-100" />
            <div v-if="ind === 2 && fileList.length > 3" class="more" @click="changeScreen('pictures')"></div>
            <span v-if="ind === 2 && fileList.length > 3" class="moreText"> +{{ fileList.length - 2 }} </span>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import "quill/dist/quill.core.css"
import "quill/dist/quill.snow.css"
import Quill from "quill"
import { quillEditor } from "vue-quill-editor"
import CustomEmojiPicker from "../../emojiPicker.vue"
import s3Upload from "../../../../utilities/imageUploader"
import { RepositoryFactory } from "../../../../repositories/RepositoryFactory"

Quill.register("modules/counter", function(quill) {
  var container = document.querySelector("#counter")
  quill.on("text-change", function() {
    var text = quill.getText()
    container.innerText = `${parseInt(text.length) - 1}/5000`
  })
})

const PostRepository = RepositoryFactory.get("posts")
const UserRepository = RepositoryFactory.get("user")

export default {
  name: "NewPostData",
  props: {
    getTextData: Function,
    addingImages: Boolean,
    handleCancelImage: Function,
    handleAddImage: Function,
    prevText: String,
    getImageData: Function,
    userName: String,
    changeScreen: Function,
    imgArray: Array,
    selectedLinkToParse: String,
    selectLink: Function,
    addLink: Function,
  },
  components: {
    quillEditor,
    CustomEmojiPicker,
  },
  computed: {
    SSO_BASE_URL: () => process.env.SSO_BASE_URL,
  },
  created() {
    window.addEventListener("mouseup", () => {
      if (this.emojiVisible) {
        setTimeout(() => {
          this.emojiVisible = false
        }, 100)
      }
    })
  },
  mounted() {
    this.postDescription = this.prevText
    // refresh addedVideo
    this.hideOptions()
  },
  data() {
    const placeholder = `What's on your mind, ${this.userName[0].toUpperCase()}${this.userName.slice(1)}?`
    return {
      linkParsingTimeout: 0,
      parsedOneLink: false,
      parsedLinkDetails: {},
      optionsShown: true,
      videoAdded: false,
      postDescription: "",
      emojiVisible: false,
      isImageUploading: false,
      fileList: [],
      editorOptions: {
        theme: "snow",
        placeholder,
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "bullet" }, { list: "ordered" }],
          ],
          counter: true,
        },
      },
    }
  },
  watch: {
    imgArray: {
      handler(v) {
        this.fileList = v
      },
      immediate: true,
      deep: true,
    },
    prevText: {
      handler(val) {
        this.postDescription = val
      },
      immediate: true,
    },
    postDescription: {
      handler(val) {
        this.getTextData(val, this.$refs.quillEditor.quill.getText().trim().length)
        clearTimeout(this.linkParsingTimeout)
        this.linkParsingTimeout = setTimeout(() => {
          this.checkForLinks(this.$refs.quillEditor.quill.getText().trim())
        }, 500)
      },
    },
    fileList: {
      handler(val) {
        this.getImageData(val)
      },
      immediate: true,
      deep: true,
    },
    addingImages: {
      handler(v) {
        if (!v) {
          this.fileList = []
          this.isImageUploading = false
        }
      },
    },
    selectedLinkToParse: {
      async handler(currentLink) {
        // console.log("handling selected link to parse")
        if (currentLink) {
          if (this.parsedLinkDetails && this.parsedLinkDetails.original_url === currentLink) return
          const res = await PostRepository.parseLink({ url: currentLink })
          if (res && res.data) {
            this.parsedLinkDetails = res.data
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    // checkLength(delta, old, source) {
    //   if (this.$refs.quillEditor.quill.getQuill().getLength() <= 5000) {
    //     return
    //   }
    //   const { ops } = delta.delta
    //   console.log({ ops })
    //   let updatedOps
    //   if (ops.length === 1) {
    //     updatedOps = [{ delete: ops[0].insert.length }]
    //   } else {
    //     updatedOps = [ops[0], { delete: ops[1].insert.length }]
    //   }
    //   console.log({ updatedOps })
    //   this.$refs.quillEditor.quill.getQuill().updateContents({ ops: updatedOps })
    // },
    openLink() {
      if (this.parsedLinkDetails && this.parsedLinkDetails.original_url) {
        window.open(this.parsedLinkDetails.original_url)
      }
    },
    checkForLinks(fullString) {
      if (!fullString) {
        return
      }
      this.addLink(null, true)
      this.parsedOneLink = false
      fullString.split(" ").forEach(async eachWord => {
        if (eachWord.startsWith("www") || eachWord.startsWith("http")) {
          try {
            if (!this.parsedOneLink) {
              this.parsedOneLink = true
              const res = await PostRepository.parseLink({ url: eachWord })
              this.parsedLinkDetails = res.data
              this.selectLink(eachWord)
            }
            // console.log("adding...", eachWord)
            this.addLink(eachWord)
          } catch (e) {
            console.log(e)
          }
        }
      })
    },
    handleQFocus() {
      // console.log("QUILL FOCUSED")
      this.showOptions()
    },
    handleQBlur() {
      // console.log("QUILL BLURED ")
      if (!this.emojiVisible) {
        setTimeout(() => {
          this.hideOptions()
        }, 100)
      }
    },
    hideOptions() {
      if (this.optionsShown && !this.emojiVisible) {
        this.$el.querySelector(".ql-toolbar").classList.add("invisible")
        this.optionsShown = false
      }
    },
    showOptions() {
      console.warn("Showing Options")
      if (!this.optionsShown) {
        this.$el.querySelector(".ql-toolbar").classList.remove("invisible")
        this.optionsShown = true
      }
    },
    getClass(i) {
      switch (i) {
        case 0:
          return "first"
        case 1:
          return "second"
        case 2:
          return "third"
        default:
          return ""
      }
    },
    async handleImageUpload() {
      const file = this.$refs.fileInput.files[0]
      try {
        if (file.type.includes("video")) {
          if (file.size / (1024 * 1024) > 50) {
            this.$notification.error({ message: "Error", description: "File size exceeds 50MB limit" })
            return
          }
          if (this.videoAdded) {
            this.$notification.error({ message: "Error", description: "Multiple videos are not allowed at the moment" })
            return
          } else {
            this.videoAdded = true
          }
        } else {
          if (file.size / (1024 * 1024) > 5) {
            this.$notification.error({ message: "Error", description: "File size exceeds 5MB limit" })
            return
          }
        }
        if (!file.type.includes("image") && !file.type.includes("video")) {
          this.$notification.error({ message: "Error", description: "Please select only image or video" })
          return
        }
        // if (file.size / (1024 * 1024) > 5) {
        //   this.$notification.error({ message: "Error", description: "File size exceeds 5MB limit" })
        // } else {
        // if (file.type.includes("video")) {
        //   if (this.videoAdded) {
        //     this.$notification.error({ message: "Error", description: "Multiple videos not allowed at the moment" })
        //     return
        //   } else {
        //     this.videoAdded = true
        //   }
        // }
        this.isImageUploading = true
        const urlData = await UserRepository.getPresignedUrl({ file_name: [file.name] })
        const fileName = urlData.data.data.filename[0]
        await s3Upload(urlData.data.data.url[0], file)
        const fileListData = { name: fileName, url: urlData.data.data.preview_url, sensitive: false, type: file.type }
        this.fileList.push(fileListData)
        this.isImageUploading = false
        // }
      } catch (e) {
        console.log(e)
      }
      this.$refs.fileInput.value = ""
    },
    handleEmojiVisible() {
      this.emojiVisible = !this.emojiVisible
    },
    insert(emoji) {
      this.handleEmojiVisible()
      // this.$refs.quillEditor.quill.insert(0, emoji)
      if (this.postDescription[this.postDescription.length - 2] === "l") {
        // for </ol> and </ul>
        this.postDescription = this.postDescription.substring(0, this.postDescription.length - 10) + emoji + this.postDescription.substring(this.postDescription.length - 10)
      } else {
        this.postDescription = this.postDescription.substring(0, this.postDescription.length - 4) + emoji + this.postDescription.substring(this.postDescription.length - 4)
      }
      this.$refs.quillEditor.quill.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
.imageContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 12px 24px;
  background: #f8fafd;
  border: 1px dashed #94a3b8;
  border-radius: 6px;
  cursor: pointer;
  min-height: 171px;
  position: relative;
}
.multiImgs {
  display: grid;
  grid-template-areas: "first first" "first first" "second third";
  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: white;
    display: none;
    max-height: 360px;
  }
  .first {
    grid-area: first;
    max-height: 360px;
  }
  .second {
    grid-area: second;
  }
  .third {
    grid-area: third;
  }
  .first,
  .second,
  .third {
    img,
    video {
      display: block !important;
    }
  }
  .more {
    background: #0f1734 !important;
    opacity: 0.6;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    cursor: pointer;
  }
  .moreText {
    cursor: pointer;
    font-weight: 700;
    position: absolute;
    font-size: 24px;
    line-height: 24px;
    color: #ffffff !important;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
.editor-emoji {
  left: 10px;
  bottom: 5px;
  @media screen and (max-width: 600px) {
    bottom: auto;
    // top: 5px;
    top: -43px;
  }
}
#counter {
  right: 0;
  bottom: 10px;
  @media screen and (max-width: 600px) {
    right: 16px;
    bottom: auto;
    // top: 10px;
    top: -40px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/home/createNewPost/data/Post.vue